import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControlLabel,
  Switch,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../../config/axiosConfig';

// Icons
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TelegramIcon from '@mui/icons-material/Telegram';
import ChatIcon from '@mui/icons-material/Chat';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LockIcon from '@mui/icons-material/Lock';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const DeliveryChannelSelection = ({ channels, onUpdateChannels, userPlan }) => {
  const navigate = useNavigate();
  const [userChannels, setUserChannels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Plan related 
  const isPremium = userPlan?.name?.toLowerCase() === 'premium';
  const isBasic = userPlan?.name?.toLowerCase() === 'basic';
  
  // Get channels that can be used based on plan
  const getAvailableChannels = () => {
    // Free plan: site only
    if (!userPlan || userPlan.name.toLowerCase() === 'free') {
      return ['site'];
    }
    
    // Basic plan: site + telegram
    if (isBasic) {
      return ['site', 'telegram'];
    }
    
    // Premium plan: all channels
    return ['site', 'email', 'telegram', 'discord', 'whatsapp'];
  };
  
  const availableChannels = getAvailableChannels();
  
  // Check if channel is available
  const isChannelAvailable = (channel) => {
    return availableChannels.includes(channel);
  };
  
  // Check if channel is configured
  const isChannelConfigured = (channel) => {
    if (!userChannels) return false;
    
    return userChannels.some(c => 
      c.type === channel && c.isVerified && c.isActive
    );
  };
  
  // Load user's configured channels
  useEffect(() => {
    const fetchUserChannels = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.get('/api/user/channels');
        setUserChannels(response.data);
      } catch (error) {
        console.error('Error fetching user channels:', error);
        setError('Unable to load your notification channel settings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserChannels();
  }, []);
  
  // Handle toggling a channel on/off
  const handleChannelToggle = (channel) => {
    // Don't toggle if channel is not available for user's plan
    if (!isChannelAvailable(channel)) return;
    
    // If turning on and channel is not configured, show setup dialog
    if (!channels[channel] && channel !== 'site' && !isChannelConfigured(channel)) {
      navigate('/account/channels');
      return;
    }
    
    // Toggle the channel
    onUpdateChannels({
      ...channels,
      [channel]: !channels[channel]
    });
  };
  
  // Open setup dialog for a channel
  const handleSetupChannel = (channel) => {
    navigate('/account/channels');
  };
  
  // Channel info cards
  const channelCards = [
    {
      id: 'site',
      name: 'Site Notifications',
      icon: <NotificationsIcon fontSize="large" />,
      description: 'Notifications will appear in your dashboard when you log in',
      color: '#2196F3', // blue
      planRequired: 'free',
      autoConfigure: true
    },
    {
      id: 'email',
      name: 'Email Notifications',
      icon: <EmailIcon fontSize="large" />,
      description: 'Receive alerts via email to your verified address',
      color: '#4CAF50', // green
      planRequired: 'premium',
      disabled: true  // disabled for launch
    },
    {
      id: 'telegram',
      name: 'Telegram Notifications',
      icon: <TelegramIcon fontSize="large" />,
      description: 'Get notifications directly to your Telegram account',
      color: '#2CA5E0', // telegram blue
      planRequired: 'basic'
    },
    {
      id: 'discord',
      name: 'Discord Notifications',
      icon: <ChatIcon fontSize="large" />,
      description: 'Receive alerts in your Discord server via webhooks',
      color: '#7289DA', // discord purple
      planRequired: 'premium'
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Notifications',
      icon: <WhatsAppIcon fontSize="large" />,
      description: 'Get alerts on WhatsApp (Coming Soon)',
      color: '#25D366', // whatsapp green
      planRequired: 'premium',
      disabled: true
    }
  ];
  
  // Render helper text based on plan
  const renderPlanHelperText = () => {
    if (isPremium) return null;
    
    if (isBasic) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Your Basic plan includes site and Telegram notifications. 
            Upgrade to Premium for access to email and Discord notifications.
          </Typography>
        </Alert>
      );
    }
    
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          Your Free plan includes site notifications only.
          Upgrade to Basic or Premium for additional notification channels.
        </Typography>
      </Alert>
    );
  };
  
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Notification Delivery Channels
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Choose how you want to receive notifications for this alert. 
        You can enable multiple channels.
      </Typography>
      
      {renderPlanHelperText()}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {channelCards.map((channel) => {
            const isAvailable = isChannelAvailable(channel.id);
            const isConfigured = channel.autoConfigure || isChannelConfigured(channel.id);
            const planName = channel.planRequired.charAt(0).toUpperCase() + channel.planRequired.slice(1);
            
            return (
              <Grid item xs={12} sm={6} md={4} key={channel.id}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    position: 'relative',
                    height: '100%',
                    p: 2,
                    overflow: 'hidden',
                    opacity: isAvailable ? 1 : 0.7,
                    borderColor: channels[channel.id] && isAvailable ? channel.color : 'divider',
                    borderWidth: channels[channel.id] && isAvailable ? 2 : 1,
                    transition: 'all 0.2s ease'
                  }}
                >
                  {!isAvailable && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                      }}
                    >
                      <Tooltip title={`Requires ${planName} Plan`}>
                        <LockIcon sx={{ fontSize: 30, opacity: 0.7 }} />
                      </Tooltip>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ color: channel.color }}>
                      {channel.icon}
                    </Box>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={(isAvailable && channels[channel.id]) || false}
                          onChange={() => handleChannelToggle(channel.id)}
                          disabled={!isAvailable || channel.disabled}
                          color="primary"
                        />
                      }
                      label=""
                    />
                  </Box>
                  
                  <Typography variant="h6" gutterBottom>
                    {channel.name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {channel.description}
                  </Typography>
                  
                  {channel.id !== 'site' && !channel.disabled && isAvailable && (
                    <Box sx={{ mt: 'auto' }}>
                      {isConfigured ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 20 }} />
                          <Typography variant="body2" color="success.main">
                            Configured
                          </Typography>
                          <Button 
                            size="small" 
                            sx={{ ml: 'auto' }} 
                            onClick={() => handleSetupChannel(channel.id)}
                          >
                            Details
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSetupChannel(channel.id)}
                          startIcon={<LinkIcon />}
                          fullWidth
                        >
                          Setup Required
                        </Button>
                      )}
                    </Box>
                  )}
                  
                  {!isAvailable && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Requires {planName} Plan
                    </Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default DeliveryChannelSelection;